/* Studio Manager · Studios & Invites
   Three sections stacked top-to-bottom:
     1. "Invite a studio" form — manual entry: name + email + optional personal note.
     2. "Pending invites" table — newest first, with resend/revoke. Tokens are masked.
     3. "Active studios" roster — who's actually onboarded.

   Lives at /admin/studio-invites. Sidebar item label: "Studios". */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, Mail, Send, RotateCcw, Trash2, Sparkles, CheckCircle2,
  Clock, XCircle, ChevronRight, Copy, RefreshCw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { copyToClipboardWithToast } from "@/lib/clipboard";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (err) {
    console.warn("studio-invites date parse failed", err);
    return iso;
  }
}

const STATUS_BADGES = {
  pending:  { label: "Pending",  cls: "bg-amber-100 text-amber-900",   Icon: Clock },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-900", Icon: CheckCircle2 },
  expired:  { label: "Expired",  cls: "bg-stone-200 text-stone-700",   Icon: XCircle },
  revoked:  { label: "Revoked",  cls: "bg-rose-100 text-rose-900",     Icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.pending;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${cfg.cls}`} data-testid={`invite-status-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function studioPublicUrl(slug) {
  // Allure stays on bare paths for SEO. Every other tenant lives under /s/{slug}.
  if (slug === "allure-image") {
    return window.location.origin;
  }
  return `${window.location.origin}/s/${slug}`;
}

export default function AdminStudioInvites() {
  const [invites, setInvites] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ studio_name: "", owner_name: "", owner_email: "", personal_note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [lastSent, setLastSent] = useState(null); // {email, studio_name}

  async function loadAll() {
    setLoading(true);
    try {
      const [i, s] = await Promise.all([
        api.get("/admin/studio-invites"),
        api.get("/admin/studios"),
      ]);
      setInvites(i.data);
      setStudios(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.studio_name || !form.owner_name || !form.owner_email) {
      return toast.error("Studio name, owner name, and email are all required.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/studio-invites", form);
      toast.success(`Invite emailed to ${data.owner_email}`);
      setLastSent({ email: data.owner_email, studio_name: data.studio_name });
      setForm({ studio_name: "", owner_name: "", owner_email: "", personal_note: "" });
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send invite");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(invite) {
    try {
      await api.post(`/admin/studio-invites/${invite.id}/resend`);
      toast.success(`Resent to ${invite.owner_email}`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend");
    }
  }

  async function handleRevoke(invite) {
    if (!window.confirm(`Revoke the invite for ${invite.owner_email}? The magic link will stop working.`)) return;
    try {
      await api.delete(`/admin/studio-invites/${invite.id}`);
      toast.success("Revoked.");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't revoke");
    }
  }

  const pendingCount = invites.filter((i) => i.effective_status === "pending").length;
  const acceptedCount = invites.filter((i) => i.effective_status === "accepted").length;

  return (
    <div className="space-y-8" data-testid="admin-studio-invites">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Lumetier · Multi-tenant beta
          </div>
          <h1 className="text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Studios & invitations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manually invite other photographers to spin up their own studio on Lumetier. Each invite is a magic link valid for 14 days.
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          data-testid="invites-refresh-btn"
          className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Active studios" value={studios.length} />
        <Stat label="Pending invites" value={pendingCount} />
        <Stat label="Accepted" value={acceptedCount} />
        <Stat label="Total invites" value={invites.length} />
      </div>

      {/* ----- Invite form ----- */}
      <section className="bg-card border border-border p-6" data-testid="invite-form-section">
        <h2 className="text-xl font-light mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Invite a new studio
        </h2>
        <p className="text-sm text-muted-foreground mb-5">
          Fill in the studio&apos;s details. We&apos;ll email them a one-click setup link.
        </p>

        {lastSent && (
          <div className="mb-5 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="invite-success-banner">
            <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
            Invite sent to <strong>{lastSent.email}</strong> ({lastSent.studio_name}). Check the &quot;Pending invites&quot; table below to track status.
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="invite-form">
          <Field
            label="Studio name"
            placeholder="Jane Smith Photography"
            value={form.studio_name}
            onChange={(v) => setForm((f) => ({ ...f, studio_name: v }))}
            testid="invite-studio-name"
            required
          />
          <Field
            label="Owner name"
            placeholder="Jane Smith"
            value={form.owner_name}
            onChange={(v) => setForm((f) => ({ ...f, owner_name: v }))}
            testid="invite-owner-name"
            required
          />
          <Field
            label="Owner email"
            type="email"
            placeholder="jane@studio.com"
            value={form.owner_email}
            onChange={(v) => setForm((f) => ({ ...f, owner_email: v }))}
            testid="invite-owner-email"
            required
            className="md:col-span-2"
          />
          <Field
            label="Personal note (optional)"
            placeholder="Hey Jane — thrilled to have you. Reach out anytime."
            value={form.personal_note}
            onChange={(v) => setForm((f) => ({ ...f, personal_note: v }))}
            testid="invite-personal-note"
            multiline
            className="md:col-span-2"
          />
          <div className="md:col-span-2 flex justify-end pt-1">
            <button
              type="submit"
              disabled={submitting}
              data-testid="invite-submit-btn"
              className="inline-flex items-center gap-2 h-10 px-6 text-xs uppercase tracking-[0.22em] bg-foreground text-background hover:opacity-90 disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" /> {submitting ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
      </section>

      {/* ----- Pending / past invites table ----- */}
      <section data-testid="invites-list-section">
        <h2 className="text-xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Invitations
        </h2>
        <div className="bg-card border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <tr>
                  <th className="text-left py-3 px-4 font-normal">Studio</th>
                  <th className="text-left py-3 px-4 font-normal">Owner</th>
                  <th className="text-left py-3 px-4 font-normal hidden md:table-cell">Sent</th>
                  <th className="text-left py-3 px-4 font-normal">Status</th>
                  <th className="py-3 px-4 font-normal w-32"></th>
                </tr>
              </thead>
              <tbody data-testid="invites-rows">
                {loading ? (
                  <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</td></tr>
                ) : invites.length === 0 ? (
                  <tr><td colSpan={5} className="py-12 text-center text-muted-foreground" data-testid="invites-empty">
                    No invitations yet. Use the form above to invite your first studio.
                  </td></tr>
                ) : (
                  invites.map((i) => (
                    <tr key={i.id} className="border-t border-border" data-testid={`invite-row-${i.id}`}>
                      <td className="py-3 px-4 font-medium">{i.studio_name}</td>
                      <td className="py-3 px-4">
                        <div>{i.owner_name}</div>
                        <a href={`mailto:${i.owner_email}`} className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1 mt-0.5">
                          <Mail className="w-3 h-3" /> {i.owner_email}
                        </a>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell text-muted-foreground">{fmtDate(i.last_emailed_at || i.created_at)}</td>
                      <td className="py-3 px-4"><StatusBadge status={i.effective_status} /></td>
                      <td className="py-3 px-4 text-right">
                        {(i.effective_status === "pending" || i.effective_status === "expired") && (
                          <button
                            onClick={() => handleResend(i)}
                            title="Resend invite email"
                            data-testid={`invite-resend-${i.id}`}
                            className="text-muted-foreground hover:text-foreground mr-3"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        {i.effective_status === "pending" && (
                          <button
                            onClick={() => handleRevoke(i)}
                            title="Revoke"
                            data-testid={`invite-revoke-${i.id}`}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {i.effective_status === "accepted" && i.accepted_studio_id && (
                          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            → {i.accepted_studio_id} <ChevronRight className="w-3 h-3" />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ----- Active studios roster ----- */}
      <section data-testid="studios-list-section">
        <h2 className="text-xl font-light mb-3 inline-flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          <Building2 className="w-5 h-5" /> Active studios
        </h2>
        <p className="text-sm text-muted-foreground mb-3">
          Every studio on Lumetier — yours included. Allure is marked as the founding studio.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="studios-grid">
          {studios.map((s) => (
            <div key={s.id} className="bg-card border border-border p-5" data-testid={`studio-card-${s.slug}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-lg font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">/{s.slug}</div>
                </div>
                <PlanBadge plan={s.plan} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground border-t border-border pt-3">
                <div><span className="block text-foreground font-medium text-sm">{s.booking_count}</span>Bookings</div>
                <div><span className="block text-foreground font-medium text-sm">{s.gallery_count}</span>Galleries</div>
              </div>
              <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-border">
                <div className="text-xs text-muted-foreground truncate" title={studioPublicUrl(s.slug)}>
                  {studioPublicUrl(s.slug).replace(/^https?:\/\//, "")}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => copyToClipboardWithToast(studioPublicUrl(s.slug), "Public URL copied")}
                    title="Copy public booking URL"
                    data-testid={`studio-copy-url-${s.slug}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a
                    href={studioPublicUrl(s.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open public site"
                    data-testid={`studio-open-url-${s.slug}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-3">
                Created {fmtDate(s.created_at)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function PlanBadge({ plan }) {
  const map = {
    founder: { cls: "bg-amber-100 text-amber-900 border-amber-300", label: "Founder" },
    beta:    { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "Beta" },
    solo:    { cls: "bg-stone-100 text-stone-700 border-stone-300", label: "Solo" },
    studio:  { cls: "bg-stone-200 text-stone-800 border-stone-400", label: "Studio" },
    pro:     { cls: "bg-foreground text-background border-foreground", label: "Pro" },
  };
  const cfg = map[plan] || map.beta;
  return (
    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border ${cfg.cls}`}>{cfg.label}</span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      <div className="text-3xl font-light mt-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required, multiline, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          rows={3}
          data-testid={testid}
          className="w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground/60 transition-colors"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          data-testid={testid}
          className="w-full bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground/60 transition-colors"
        />
      )}
    </label>
  );
}
